import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../firebase';
import { useSearchParams } from 'react-router-dom';
import TopNavigation from '../components/TopNavigation';
import LeftNavSidebar from '../components/LeftNavSidebar';
import RightWidgetsSidebar from '../components/RightWidgetsSidebar';
import ImageWithFallback from '../components/ImageWithFallback';
import OnlineDot, { PeerStatus } from '../components/OnlineDot';
import UserLink from '../components/UserLink';
import VerifiedBadge from '../components/VerifiedBadge';
import { deriveVerifiedStatus } from '../utils/verification';
import { validateContent, filterProfanity } from '../utils/contentModeration';
import ChatAttachment from '../components/ChatAttachment';
import { startCall } from '../components/CallManager';
import { subscribeRealtime } from '../utils/realtime';
import { uploadModeratedMedia } from '../utils/moderatedUpload';
import { describeModerationError } from '../utils/moderationErrors';
import { requestUploadUrl, uploadFileToSignedUrl } from '../utils/mediaApi';
import { acceptFriendRequest, ChatAttachmentInput, createConversation, declineFriendRequest, deleteChatMessage, editChatMessage, FriendUser, getUserById, getUserMe, listConversations, listFriendRequests, listFriends, listMessages, searchUsers as searchUsersApi, sendFriendRequest, sendMessage } from '../utils/secureFunctions';
import { appConfirm } from '../utils/dialog';
import { getProfileUrl } from '../utils/urls';

const describeMessagingError = (err: unknown): string => {
  const msg = String((err as any)?.message || '');
  if (msg.includes('FRIENDS_REQUIRED')) return 'FRIENDS_REQUIRED';
  if (msg.includes('USER_BLOCKED')) return 'You cannot message this user.';
  if (msg.includes('MESSAGING_DISABLED')) return 'This user is not accepting messages.';
  if (msg.includes('USER_NOT_FOUND')) return 'This user could not be found.';
  return 'Could not open this chat. Please try again.';
};

interface Conversation {
  participantVerified?: { [uid: string]: boolean };
  id: string;
  participants: string[];
  participantNames: { [uid: string]: string };
  participantPhotos: { [uid: string]: string };
  lastMessage: string;
  lastMessageTime: any;
  unreadCount: { [uid: string]: number };
}

interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  content: string;
  timestamp: any;
  read: boolean;
  attachmentURL?: string | null;
  attachmentType?: string | null;
  attachmentName?: string | null;
  attachmentSize?: number | null;
  durationSec?: number | null;
  editedAt?: string | null;
  deletedAt?: string | null;
}

const MB = 1024 * 1024;
const BLOCKED_EXT = /\.(exe|msi|bat|cmd|com|scr|ps1|vbs|js|jar|apk|app|dmg|sh|html?|svg)$/i;
const SAFE_DOC_TYPES = /^(application\/(pdf|zip|x-zip-compressed|msword|vnd\.[\w.+-]+|rtf)|text\/(plain|csv))$/;

function classifyFile(file: File): ChatAttachmentInput['type'] {
  if (file.type.startsWith('image/') && file.type !== 'image/svg+xml') return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  return 'file';
}

function pickRecorderMime(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  const MR = (window as any).MediaRecorder;
  return candidates.find((m) => MR?.isTypeSupported?.(m)) || '';
}

interface User {
  uid: string;
  name: string;
  username: string;
  photoURL: string;
  verifiedFlag?: boolean;
}

const Messaging: React.FC = () => {
  
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const deepLinkHandledRef = useRef<string | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [verifiedCache] = useState<Record<string, boolean>>({});
  const [newMessage, setNewMessage] = useState('');
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [searchedUsers, setSearchedUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [recording, setRecording] = useState<{ startedAt: number } | null>(null);
  const [recordSecs, setRecordSecs] = useState(0);
  const recorderRef = useRef<{ rec: MediaRecorder; chunks: Blob[]; stream: MediaStream; cancelled: boolean } | null>(null);
  const [friends, setFriends] = useState<FriendUser[]>([]);
  const [friendRequests, setFriendRequests] = useState<Array<{ id: string; user: FriendUser }>>([]);
  const [requestBusy, setRequestBusy] = useState<string | null>(null);

  const loadFriends = async () => {
    try {
      const [f, r] = await Promise.all([listFriends(), listFriendRequests()]);
      setFriends(f.friends || []);
      setFriendRequests(r.requests || []);
    } catch (e) {
      console.error('Failed to load friends:', e);
    }
  };

  useEffect(() => {
    if (!auth.currentUser) return;
    loadFriends();
    const interval = setInterval(loadFriends, 30000);
    return () => clearInterval(interval);
  }, []);

  const respondToRequest = async (id: string, accept: boolean) => {
    if (requestBusy) return;
    setRequestBusy(id);
    try {
      if (accept) await acceptFriendRequest(id);
      else await declineFriendRequest(id);
      await loadFriends();
    } catch (e) {
      alert('Could not update this friend request. Please try again.');
    } finally {
      setRequestBusy(null);
    }
  };

  useEffect(() => {
    if (!auth.currentUser) return;
    let active = true;
    const load = async () => {
      try {
        const res = await listConversations();
        if (!active) return;
        setConversations(res.conversations as Conversation[]);
        setConversationsLoaded(true);
      } catch (e) {
        console.error('Failed to load conversations:', e);
      }
    };
    load();
    const interval = setInterval(load, 15000);
    return () => { active = false; clearInterval(interval); };
  }, []);

  // Handle URL parameter for direct conversation linking
  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    if (conversationId && conversations.length > 0) {
      const targetConv = conversations.find(c => c.id === conversationId);
      if (targetConv) {
        setSelectedConversation(targetConv);
      }
    }
  }, [searchParams, conversations]);

  // Deep link from a profile: /messages?user=<uid> opens (or creates) a chat with that user.
  useEffect(() => {
    const targetUid = searchParams.get('user');
    if (!targetUid || !conversationsLoaded || !auth.currentUser) return;
    if (targetUid === auth.currentUser.uid || deepLinkHandledRef.current === targetUid) return;
    deepLinkHandledRef.current = targetUid;
    (async () => {
      try {
        const res = await getUserById(targetUid);
        const p: any = res.profile || {};
        await handleStartConversation({
          uid: targetUid,
          name: p.name || 'User',
          username: p.username || '',
          photoURL: p.photoURL || '',
        });
      } catch (e) {
        console.error('Failed to open conversation from link', e);
        alert('Could not open this chat. Please try again.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, conversationsLoaded]);

  // Only auto-scroll on first open, when I send, or when something new arrives while I'm already at the bottom.
  const nearBottomRef = useRef(true);
  const lastMsgKeyRef = useRef('');
  const applyMessages = (list: Message[], force = false) => {
    const last = list[list.length - 1];
    const key = `${list.length}:${last?.id || ''}`;
    const changed = key !== lastMsgKeyRef.current;
    lastMsgKeyRef.current = key;
    setMessages(list);
    if (force || (changed && (nearBottomRef.current || last?.senderId === auth.currentUser?.uid))) {
      setTimeout(scrollToBottom, 0);
    }
  };

  useEffect(() => {
    if (!selectedConversation) return;
    let active = true;
    let first = true;
    lastMsgKeyRef.current = '';
    nearBottomRef.current = true;
    const load = async () => {
      try {
        const res = await listMessages(selectedConversation.id);
        if (!active) return;
        applyMessages(res.messages as Message[], first);
        first = false;
      } catch (e) {
        console.error('Failed to load messages:', e);
      }
    };
    load();
    const interval = setInterval(load, 5000);
    return () => { active = false; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const selectedIdRef = useRef<string | null>(null);
  selectedIdRef.current = selectedConversation?.id || null;

  const refreshChat = async (conversationId?: string | null) => {
    try {
      const convRes = await listConversations();
      setConversations(convRes.conversations as Conversation[]);
      const id = conversationId ?? selectedIdRef.current;
      if (id && id === selectedIdRef.current) {
        const res = await listMessages(id);
        applyMessages(res.messages as Message[]);
      }
    } catch {
      // polling will catch up
    }
  };

  useEffect(() => subscribeRealtime((p) => {
    if (p?.type === 'chat_message' || p?.type === 'chat_updated') refreshChat(p.conversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const replaceMessage = (m: Message) => setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, ...m } : x)));

  const startEdit = (msg: Message) => {
    setMenuFor(null);
    setEditingId(msg.id);
    setEditText(msg.content || '');
  };

  const saveEdit = async (msg: Message) => {
    if (!selectedConversation) return;
    const text = editText.trim();
    if (!text && !msg.attachmentURL) { alert('A message cannot be empty. Use Delete to remove it.'); return; }
    if (text === (msg.content || '').trim()) { setEditingId(null); return; }
    const check = validateContent(text);
    if (text && !check.isValid) { alert(check.message || 'This message is not allowed.'); return; }
    setSavingEdit(true);
    try {
      const res = await editChatMessage(selectedConversation.id, msg.id, filterProfanity(text));
      replaceMessage(res.message as Message);
      setEditingId(null);
    } catch (e) {
      console.error('Edit failed', e);
      alert('Could not edit this message. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  const removeMessage = async (msg: Message) => {
    setMenuFor(null);
    if (!selectedConversation) return;
    if (!await appConfirm('Delete this message for everyone? This cannot be undone.')) return;
    try {
      const res = await deleteChatMessage(selectedConversation.id, msg.id);
      replaceMessage(res.message as Message);
    } catch (e) {
      console.error('Delete failed', e);
      alert('Could not delete this message. Please try again.');
    }
  };

  const reportSendError = (error: unknown) => {
    console.error('Failed to send message:', error);
    const moderation = describeModerationError(error);
    if (moderation) { alert(moderation); return; }
    const text = describeMessagingError(error);
    alert(text === 'FRIENDS_REQUIRED' ? 'You need to be friends with this person to send messages. Send them a friend request from their profile.' : 'Failed to send message');
  };

  const sendAttachment = async (file: File, opts: { durationSec?: number; name?: string } = {}) => {
    if (!selectedConversation || !auth.currentUser) return;
    if (uploadPct !== null) { alert('Please wait for the current upload to finish.'); return; }
    const type = classifyFile(file);
    const name = (opts.name || file.name || 'file').slice(0, 200);
    if (type === 'file' && BLOCKED_EXT.test(name)) { alert('This file type cannot be shared for safety reasons.'); return; }
    const limit = type === 'video' ? 100 * MB : type === 'image' ? 15 * MB : 25 * MB;
    if (file.size > limit) { alert(`This file is too large. The limit is ${Math.round(limit / MB)} MB.`); return; }
    const conversationId = selectedConversation.id;
    setUploadPct(0);
    try {
      let attachment: ChatAttachmentInput;
      let moderationTokens: Record<string, unknown> | null = null;
      if (type === 'image' || type === 'video') {
        const res = await uploadModeratedMedia({ folder: 'chat', field: 'attachment', file, onProgress: (p) => setUploadPct(Math.round(p)) });
        attachment = { url: res.objectKey, type, name, size: file.size };
        moderationTokens = { attachment: res.proof };
      } else {
        // Risky types are stored as plain downloads so they can never render as a web page.
        const contentType = type === 'audio' ? (file.type || 'audio/webm') : (SAFE_DOC_TYPES.test(file.type) ? file.type : 'application/octet-stream');
        const safeName = name.replace(/[^a-zA-Z0-9._-]/g, '-');
        const signed = await requestUploadUrl({ filename: `chat-${safeName}`, contentType, size: file.size });
        await uploadFileToSignedUrl({ uploadUrl: signed.uploadUrl, file, headers: signed.requiredHeaders, onProgress: (p) => setUploadPct(Math.round(p)) });
        attachment = { url: signed.objectKey, type, name, size: file.size, durationSec: opts.durationSec ?? null };
      }
      const me = await getUserMe();
      await sendMessage(conversationId, { content: '', senderName: me.profile?.name || 'Anonymous', attachment, moderationTokens });
      await refreshChat(conversationId);
    } catch (error) {
      reportSendError(error);
    } finally {
      setUploadPct(null);
    }
  };

  const onPickFiles = async (files: FileList | File[] | null) => {
    const list = Array.from(files || []).slice(0, 5);
    for (const f of list) {
      // eslint-disable-next-line no-await-in-loop
      await sendAttachment(f);
    }
  };

  const startRecording = async () => {
    if (recording || !selectedConversation) return;
    if (!navigator.mediaDevices?.getUserMedia || !(window as any).MediaRecorder) {
      alert('Voice messages are not supported in this browser.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const mimeType = pickRecorderMime();
      const rec = new MediaRecorder(stream, mimeType ? { mimeType, audioBitsPerSecond: 48000 } : undefined);
      const state = { rec, chunks: [] as Blob[], stream, cancelled: false };
      recorderRef.current = state;
      const startedAt = Date.now();
      rec.ondataavailable = (e) => { if (e.data.size) state.chunks.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(null);
        recorderRef.current = null;
        if (state.cancelled || !state.chunks.length) return;
        const type = rec.mimeType || mimeType || 'audio/webm';
        const ext = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm';
        const blob = new Blob(state.chunks, { type: type.split(';')[0] });
        const secs = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
        if (secs < 1) return;
        const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: type.split(';')[0] });
        void sendAttachment(file, { durationSec: secs, name: `Voice message (${secs}s)` });
      };
      rec.start(250);
      setRecordSecs(0);
      setRecording({ startedAt });
    } catch (e: any) {
      const name = e?.name || '';
      alert(name === 'NotAllowedError'
        ? 'Please allow microphone access for belivah.com in your browser (click the lock icon by the address bar), then try again.'
        : name === 'NotFoundError'
          ? 'No microphone was found on this device.'
          : name === 'NotReadableError' || name === 'AbortError'
            ? 'Your microphone is being used by another app. Close it and try again.'
            : 'Could not start recording.');
    }
  };

  const stopRecording = (cancel: boolean) => {
    const r = recorderRef.current;
    if (!r) return;
    r.cancelled = cancel;
    if (r.rec.state !== 'inactive') r.rec.stop();
  };

  useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => {
      const secs = Math.round((Date.now() - recording.startedAt) / 1000);
      setRecordSecs(secs);
      if (secs >= 300) stopRecording(false);
    }, 500);
    return () => clearInterval(t);
  }, [recording]);

  useEffect(() => () => stopRecording(true), []);

  const handleSendMessage = async () => {
    if (!selectedConversation || !auth.currentUser) return;
    if (!newMessage.trim()) return;
    const vm = validateContent(newMessage);
    if (!vm.isValid) { alert(vm.message); return; }

    try {
      const me = await getUserMe();
      const senderName = me.profile?.name || 'Anonymous';
      const safeMessage = filterProfanity(newMessage);

      await sendMessage(selectedConversation.id, {
        content: safeMessage,
        senderName,
      });

      setNewMessage('');
      await refreshChat(selectedConversation.id);
    } catch (error) {
      reportSendError(error);
    }
  };

  const handleSearchUsers = async () => {
    if (!searchQuery.trim()) {
      setSearchedUsers([]);
      return;
    }

    try {
      const res = await searchUsersApi({ q: searchQuery, limit: 30 });
      const foundUsers: User[] = (res.users || [])
        .filter((u: any) => u.uid !== auth.currentUser?.uid)
        .map((u: any) => ({
          uid: u.uid,
          name: u.name,
          username: u.username,
          photoURL: u.photoURL,
          verifiedFlag: deriveVerifiedStatus(u as any),
        }));
      setSearchedUsers(foundUsers);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleStartConversation = async (user: User) => {
    if (!auth.currentUser) return;

    try {
      // Check if conversation exists
      const existingConv = conversations.find(c => 
        c.participants.includes(user.uid) && c.participants.includes(auth.currentUser!.uid)
      );

      if (existingConv) {
        setSelectedConversation(existingConv);
        setShowNewChat(false);
        return;
      }

      // Create new conversation
      const me = await getUserMe();
      const currentUserName = me.profile?.name || 'Anonymous';
      const currentUserPhoto = me.profile?.photoURL || '';

      const res = await createConversation({
        participantId: user.uid,
        participantName: user.name,
        participantPhoto: user.photoURL,
        senderName: currentUserName,
        senderPhoto: currentUserPhoto,
      });

      const conv = res.conversation as Conversation;
      setConversations((prev) => (prev.some((c) => c.id === conv.id) ? prev : [conv, ...prev]));
      setSelectedConversation(conv);
      setShowNewChat(false);
    } catch (error) {
      console.error('Failed to start conversation:', error);
      const text = describeMessagingError(error);
      if (text === 'FRIENDS_REQUIRED') {
        if (await appConfirm(`You need to be friends with ${user.name} before you can message them. Send a friend request now?`)) {
          try {
            const r = await sendFriendRequest(user.uid);
            alert(r.status === 'friends' ? `You and ${user.name} are now friends. Tap them again to chat.` : 'Friend request sent. You can chat once it is accepted.');
            loadFriends();
          } catch {
            alert('Could not send the friend request. Please try again.');
          }
        }
      } else {
        alert(text);
      }
    }
  };

  const getOtherParticipant = (conversation: Conversation) => {
    const otherUid = conversation.participants.find(uid => uid !== auth.currentUser?.uid);
    return {
      uid: otherUid || '',
      name: otherUid ? conversation.participantNames[otherUid] : 'Unknown',
      photo: otherUid ? conversation.participantPhotos[otherUid] : ''
    };
  };

  const [isMobile, setIsMobile] = useState<boolean>(() => {
    try { return window.innerWidth <= 768; } catch { return false; }
  });
  useEffect(() => {
    const onResize = () => { try { setIsMobile(window.innerWidth <= 768); } catch {} };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div style={styles.container}>
      <TopNavigation />
      {!isMobile && <LeftNavSidebar />}
      {!isMobile && <RightWidgetsSidebar />}
      <div style={{
        ...styles.contentArea,
        ...(isMobile ? { marginLeft: 0, marginRight: 0, paddingLeft: 12, paddingRight: 12, paddingTop: 16 } : {})
      }}>
      <header style={styles.pageHeader}>
        <h1 style={styles.logo}>Messages</h1>
        <button onClick={() => setShowNewChat(true)} style={styles.newChatButton}>
          + New Chat
        </button>
      </header>

      <div style={{
        ...styles.messagesContainer,
        ...(isMobile ? { flexDirection: 'column', height: 'auto', minHeight: 'calc(100vh - 120px)' } : {})
      }}>
        {!isMobile || !selectedConversation ? (
        <div style={{
          ...styles.conversationsList,
          ...(isMobile ? { width: '100%', borderRight: 'none', borderBottom: '2px solid #E0E0E0' } : {})
        }}>
          {friendRequests.length > 0 && (
            <div style={{ marginBottom: 12, padding: 10, borderRadius: 12, background: 'rgba(108,60,225,0.06)', border: '1px solid rgba(108,60,225,0.18)' }}>
              <h3 style={{ ...styles.conversationsTitle, marginTop: 0 }}>Friend requests ({friendRequests.length})</h3>
              {friendRequests.map((r) => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                  <ImageWithFallback src={r.user.photoURL || ''} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  <a href={getProfileUrl({ uid: r.user.uid, username: r.user.username })} style={{ flex: 1, minWidth: 0, fontWeight: 600, color: 'inherit', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.user.name || 'User'}</a>
                  <button disabled={requestBusy === r.id} onClick={() => respondToRequest(r.id, true)} style={{ padding: '6px 12px', borderRadius: 999, border: 'none', background: '#6C3CE1', color: '#fff', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>Accept</button>
                  <button disabled={requestBusy === r.id} onClick={() => respondToRequest(r.id, false)} style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #ddd', background: '#fff', color: '#444', cursor: 'pointer', whiteSpace: 'nowrap' }}>Decline</button>
                </div>
              ))}
            </div>
          )}
          <h3 style={styles.conversationsTitle}>Conversations</h3>
          {conversations.length === 0 ? (
            <p style={styles.emptyText}>No conversations yet</p>
          ) : (
            conversations.map((conv) => {
              const other = getOtherParticipant(conv);
              return (
                <div
                  key={conv.id}
                  onClick={() => setSelectedConversation(conv)}
                  style={{
                    ...styles.conversationItem,
                    ...(selectedConversation?.id === conv.id ? styles.selectedConversation : {})
                  }}
                >
                  <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                    <ImageWithFallback src={other.photo} alt="" style={styles.conversationAvatar} />
                    <OnlineDot userId={other.uid} />
                  </span>
                  <div style={styles.conversationInfo}>
                    <strong style={styles.conversationName}>
                      {other.name}
                      {(() => {
                        const otherUid = conv.participants.find(uid => uid !== auth.currentUser?.uid);
                        const flag = otherUid ? (conv.participantVerified?.[otherUid] ?? verifiedCache[otherUid]) : false;
                        return flag ? <VerifiedBadge size={14} /> : null;
                      })()}
                    </strong>
                    <p style={styles.conversationLastMessage}>{conv.lastMessage || 'Start chatting...'}</p>
                  </div>
                  {(() => {
                    const n = Number(conv.unreadCount?.[auth.currentUser?.uid || ''] || 0);
                    if (n <= 0 || selectedConversation?.id === conv.id) return null;
                    return (
                      <span
                        aria-label={`${n} unread`}
                        style={{ marginLeft: 'auto', minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10, background: 'var(--color-primary-600, #6C3CE1)', color: '#fff', fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        {n > 99 ? '99+' : n}
                      </span>
                    );
                  })()}
                </div>
              );
            })
          )}
        </div>
        ) : null}

        {(!isMobile || !!selectedConversation) && (
        <div style={{
          ...styles.chatArea,
          ...(isMobile ? { width: '100%' } : {})
        }}>
          {selectedConversation ? (
            <>
              <div style={styles.chatHeader}>
                {isMobile && (
                  <button onClick={() => setSelectedConversation(null)} style={{ background: 'transparent', border: 'none', fontSize: 18, marginRight: 8, cursor: 'pointer' }}>←</button>
                )}
                <UserLink userId={getOtherParticipant(selectedConversation).uid} style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                  <ImageWithFallback
                    src={getOtherParticipant(selectedConversation).photo}
                    alt=""
                    style={styles.chatHeaderAvatar}
                  />
                  <OnlineDot userId={getOtherParticipant(selectedConversation).uid} size={11} />
                </UserLink>
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <UserLink as="strong" userId={getOtherParticipant(selectedConversation).uid} style={styles.chatHeaderName}>
                  {getOtherParticipant(selectedConversation).name}
                  {(() => {
                    const otherUid = selectedConversation.participants.find(uid => uid !== auth.currentUser?.uid);
                    const flag = otherUid ? (selectedConversation.participantVerified?.[otherUid] ?? verifiedCache[otherUid]) : false;
                    return flag ? <VerifiedBadge size={16} /> : null;
                  })()}
                </UserLink>
                <PeerStatus userId={getOtherParticipant(selectedConversation).uid} />
                </div>
                {(() => {
                  const otherUid = selectedConversation.participants.find(uid => uid !== auth.currentUser?.uid);
                  if (!otherUid) return null;
                  const peer = { uid: otherUid, name: getOtherParticipant(selectedConversation).name, photoURL: getOtherParticipant(selectedConversation).photo };
                  return (
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                      <button type="button" title="Voice call" aria-label="Voice call" onClick={() => startCall(peer, false)} style={styles.headerIconBtn}>📞</button>
                      <button type="button" title="Video call" aria-label="Video call" onClick={() => startCall(peer, true)} style={styles.headerIconBtn}>🎥</button>
                    </div>
                  );
                })()}
              </div>

              <div
                style={{ ...styles.messagesArea, ...(dragOver ? { outline: '2px dashed #6c5ce7', outlineOffset: -6 } : {}) }}
                onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDragOver(true); } }}
                onScroll={(e) => {
                  const el = e.currentTarget;
                  nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); void onPickFiles(e.dataTransfer.files); }}
              >
                {messages.map((msg) => {
                  const mine = msg.senderId === auth.currentUser?.uid;
                  if (msg.attachmentType === 'call') {
                    return (
                      <div key={msg.id} style={{ display: 'flex', justifyContent: 'center', margin: '6px 0' }}>
                        <span style={styles.callRecord}>{msg.content}</span>
                      </div>
                    );
                  }
                  const deleted = !!msg.deletedAt;
                  const editing = editingId === msg.id;
                  const canChange = mine && !deleted;
                  return (
                    <div
                      key={msg.id}
                      style={{ ...styles.messageWrapper, justifyContent: mine ? 'flex-end' : 'flex-start', alignItems: 'center', gap: 4, position: 'relative' }}
                    >
                      {canChange && !editing && (
                        <button
                          type="button"
                          aria-label="Message options"
                          title="Edit or delete"
                          onClick={() => setMenuFor((m) => (m === msg.id ? null : msg.id))}
                          style={styles.msgMenuBtn}
                        >⋮</button>
                      )}
                      {menuFor === msg.id && (
                        <div role="menu" style={styles.msgMenu}>
                          <button type="button" role="menuitem" style={styles.msgMenuItem} onClick={() => startEdit(msg)}>✏️ Edit</button>
                          <button type="button" role="menuitem" style={{ ...styles.msgMenuItem, color: '#C62828' }} onClick={() => removeMessage(msg)}>🗑️ Delete</button>
                        </div>
                      )}
                      <div style={{ ...styles.messageBubble, ...(mine ? styles.myMessage : styles.theirMessage), ...(msg.attachmentURL && !editing ? { padding: 6 } : {}), ...(deleted ? styles.deletedMessage : {}) }}>
                        {deleted ? (
                          <span>🚫 This message was deleted</span>
                        ) : editing ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 200 }}>
                            <textarea
                              autoFocus
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void saveEdit(msg); }
                                if (e.key === 'Escape') setEditingId(null);
                              }}
                              rows={2}
                              maxLength={4000}
                              aria-label="Edit message"
                              style={styles.editInput}
                            />
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                              <button type="button" onClick={() => setEditingId(null)} style={styles.editCancelBtn}>Cancel</button>
                              <button type="button" disabled={savingEdit} onClick={() => saveEdit(msg)} style={styles.editSaveBtn}>{savingEdit ? 'Saving…' : 'Save'}</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {msg.attachmentURL && <ChatAttachment data={msg} mine={mine} />}
                            {msg.content && <div style={msg.attachmentURL ? { padding: '6px 6px 2px' } : undefined}>{msg.content}</div>}
                          </>
                        )}
                        {msg.editedAt && !deleted && !editing && (
                          <div style={{ fontSize: 11, opacity: 0.75, textAlign: 'right', padding: msg.attachmentURL ? '0 6px 2px' : 0 }}>edited</div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              <div style={{
                ...styles.inputArea,
                ...(isMobile ? styles.inputAreaMobile : {})
              }}>
                {uploadPct !== null && (
                  <div style={styles.uploadBar} aria-live="polite">
                    <span style={{ fontSize: 12, color: '#555' }}>Sending… {uploadPct}%</span>
                    <div style={styles.uploadTrack}><div style={{ ...styles.uploadFill, width: `${uploadPct}%` }} /></div>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rtf"
                  onChange={(e) => { const files = e.target.files ? Array.from(e.target.files) : []; e.target.value = ''; void onPickFiles(files); }}
                />
                {recording ? (
                  <div style={{ ...styles.composerSurface, ...(isMobile ? styles.composerSurfaceMobile : {}) }}>
                    <button type="button" onClick={() => stopRecording(true)} style={styles.composerIconBtn} title="Cancel" aria-label="Cancel recording">🗑️</button>
                    <span style={styles.recordingLabel}>
                      <span style={styles.recordDot} /> Recording {Math.floor(recordSecs / 60)}:{String(recordSecs % 60).padStart(2, '0')}
                    </span>
                    <button type="button" onClick={() => stopRecording(false)} style={{ ...styles.sendButton, ...(isMobile ? styles.sendButtonMobile : {}) }}>
                      Send
                    </button>
                  </div>
                ) : (
                <div style={{ ...styles.composerSurface, ...(isMobile ? styles.composerSurfaceMobile : {}) }}>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadPct !== null}
                    style={styles.composerIconBtn}
                    title="Share photo, video or file"
                    aria-label="Attach file"
                  >📎</button>
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    onPaste={(e) => {
                      const files = Array.from(e.clipboardData?.files || []);
                      if (files.length) { e.preventDefault(); void onPickFiles(files); }
                    }}
                    placeholder="Type a message..."
                    style={{ ...styles.messageInput, ...(isMobile ? styles.messageInputMobile : {}) }}
                  />
                  {newMessage.trim() ? (
                    <button
                      onClick={handleSendMessage}
                      style={{ ...styles.sendButton, ...(isMobile ? styles.sendButtonMobile : {}) }}
                    >
                      Send
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={startRecording}
                      disabled={uploadPct !== null}
                      style={styles.composerIconBtn}
                      title="Record voice message"
                      aria-label="Record voice message"
                    >🎤</button>
                  )}
                </div>
                )}
              </div>
            </>
          ) : (
            <div style={styles.emptyChat}>
              <p style={styles.emptyChatText}>Select a conversation or start a new chat</p>
            </div>
          )}
        </div>
        )}
      </div>

      {showNewChat && (
        <div style={styles.overlay} onClick={() => setShowNewChat(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>New Chat</h3>
              <button onClick={() => setShowNewChat(false)} style={styles.closeButton}>✕</button>
            </div>

            <div style={styles.modalContent}>
              <div style={styles.searchSection}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search users by name or username..."
                  style={styles.searchInput}
                />
                <button onClick={handleSearchUsers} style={styles.searchButton}>Search</button>
              </div>

              <div style={styles.usersList}>
                {searchedUsers.length === 0 ? (
                  friends.length === 0 ? (
                    <p style={styles.emptyText}>Add friends to start chatting. Search for someone, then send them a friend request.</p>
                  ) : (
                    <>
                      <p style={{ ...styles.emptyText, textAlign: 'left', margin: '4px 0 8px' }}>Your friends</p>
                      {friends.map((f) => (
                        <div key={f.uid} onClick={() => handleStartConversation({ uid: f.uid, name: f.name || 'User', username: f.username || '', photoURL: f.photoURL || '' })} style={styles.userItem}>
                          <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                            <ImageWithFallback src={f.photoURL || ''} alt="" style={styles.userAvatar} />
                            <OnlineDot userId={f.uid} />
                          </span>
                          <div>
                            <strong style={styles.userName}>{f.name || 'User'}</strong>
                            {f.username && <p style={styles.userUsername}>@{f.username}</p>}
                          </div>
                        </div>
                      ))}
                    </>
                  )
                ) : (
                  searchedUsers.map((user) => (
                    <div
                      key={user.uid}
                      onClick={() => handleStartConversation(user)}
                      style={styles.userItem}
                    >
                      <ImageWithFallback src={user.photoURL} alt="" style={styles.userAvatar} />
                      <div>
                        <strong style={styles.userName}>
                        {user.name}
                        {user.verifiedFlag ? <VerifiedBadge size={14} /> : null}
                      </strong>
                        <p style={styles.userUsername}>@{user.username}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: 'var(--color-background-alt)',
  },
  contentArea: {
    paddingTop: '16px',
    marginLeft: '300px',
    marginRight: '340px',
    paddingLeft: '20px',
    paddingRight: '20px',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px 30px',
    backgroundColor: '#0B6B6F',
    color: '#FFFFFF',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  logo: {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
  },
  newChatButton: {
    padding: '8px 16px',
    backgroundColor: '#66EAD1',
    color: '#072B2C',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  messagesContainer: {
    display: 'flex',
    flex: 1,
    height: 'calc(100vh - 70px)',
  },
  conversationsList: {
    width: '300px',
    backgroundColor: '#FFFFFF',
    borderRight: '2px solid #E0E0E0',
    overflowY: 'auto',
  },
  conversationsTitle: {
    padding: '15px 20px',
    margin: 0,
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#072B2C',
    borderBottom: '1px solid #E0E0E0',
  },
  conversationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '15px 20px',
    cursor: 'pointer',
    borderBottom: '1px solid #F5EDE1',
    transition: 'background 0.2s',
  },
  selectedConversation: {
    backgroundColor: '#F5EDE1',
  },
  conversationAvatar: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  conversationInfo: {
    flex: 1,
    overflow: 'hidden',
  },
  conversationName: {
    fontSize: '16px',
    color: '#072B2C',
    marginBottom: '4px',
  },
  conversationLastMessage: {
    fontSize: '13px',
    color: '#5B6E6E',
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  chatArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    minHeight: 0,
  },
  chatHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '15px 20px',
    borderBottom: '2px solid #E0E0E0',
    // Stays under the fixed top bar even when the page itself scrolls (e.g. on phones).
    position: 'sticky',
    top: 64,
    zIndex: 5,
    backgroundColor: '#FFFFFF',
  },
  chatHeaderAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  chatHeaderName: {
    fontSize: '18px',
    color: '#072B2C',
  },
  messagesArea: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  messageWrapper: {
    display: 'flex',
  },
  messageBubble: {
    maxWidth: '70%',
    padding: '10px 15px',
    borderRadius: '18px',
    fontSize: '15px',
    lineHeight: '1.4',
    wordWrap: 'break-word',
  },
  myMessage: {
    backgroundColor: '#17A2A9',
    color: '#FFFFFF',
  },
  theirMessage: {
    backgroundColor: '#F5EDE1',
    color: '#072B2C',
  },
  deletedMessage: {
    backgroundColor: 'transparent',
    color: '#7A8A8A',
    fontStyle: 'italic',
    border: '1px dashed #CFD8D8',
  },
  msgMenuBtn: {
    background: 'transparent',
    border: 'none',
    color: '#7A8A8A',
    fontSize: 18,
    lineHeight: 1,
    cursor: 'pointer',
    padding: '4px 6px',
    borderRadius: 6,
  },
  msgMenu: {
    position: 'absolute',
    right: 24,
    top: '100%',
    zIndex: 6,
    display: 'flex',
    flexDirection: 'column',
    background: '#FFFFFF',
    border: '1px solid #E0E0E0',
    borderRadius: 10,
    boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
    overflow: 'hidden',
    minWidth: 130,
  },
  msgMenuItem: {
    background: 'transparent',
    border: 'none',
    textAlign: 'left',
    padding: '10px 14px',
    fontSize: 14,
    cursor: 'pointer',
    color: '#072B2C',
  },
  editInput: {
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.6)',
    padding: '8px 10px',
    fontSize: 15,
    fontFamily: 'inherit',
    resize: 'vertical',
    color: '#072B2C',
    background: '#FFFFFF',
  },
  editCancelBtn: {
    background: 'rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.6)',
    color: 'inherit',
    borderRadius: 999,
    padding: '4px 12px',
    cursor: 'pointer',
    fontSize: 13,
  },
  editSaveBtn: {
    background: '#FFFFFF',
    border: 'none',
    color: '#0B6B6F',
    fontWeight: 700,
    borderRadius: 999,
    padding: '4px 14px',
    cursor: 'pointer',
    fontSize: 13,
  },
  inputArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    justifyContent: 'center',
    padding: '12px 14px',
    borderTop: '2px solid #E0E0E0',
    position: 'sticky' as const,
    bottom: 0,
    background: '#FFFFFF',
    zIndex: 2,
  },
  inputAreaMobile: {
    borderTop: 'none',
    padding: '16px 10px 24px',
    background: 'transparent',
  },
  composerSurface: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    backgroundColor: '#F5F7F7',
    borderRadius: '999px',
    border: '1px solid #E0E0E0',
    gap: '6px',
    padding: '6px 8px 6px 8px',
    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.08)',
  },
  composerSurfaceMobile: {
    position: 'static',
    gap: '12px',
    padding: '12px 14px',
  },
  messageInput: {
    flex: 1,
    width: '100%',
    minWidth: 0,
    padding: '0 8px',
    fontSize: '16px',
    borderRadius: '999px',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    minHeight: '44px',
  },
  messageInputMobile: {
    flex: 1,
    width: '100%',
    padding: '0 12px',
    minHeight: '46px',
    borderRadius: '16px',
  },
  sendButton: {
    minWidth: '78px',
    height: '40px',
    backgroundColor: '#17A2A9',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '999px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 10px 24px rgba(23, 162, 169, 0.35)',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    flexShrink: 0,
  },
  sendButtonMobile: {
    position: 'static',
    minWidth: '72px',
    height: '44px',
    transform: 'none',
    marginLeft: '4px',
    padding: '0 20px',
    alignSelf: 'stretch',
  },
  headerIconBtn: {
    width: 40, height: 40, borderRadius: '50%', border: '1px solid #E0E0E0', background: '#F5F7F7',
    cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
  },
  composerIconBtn: {
    width: 40, height: 40, borderRadius: '50%', border: 'none', background: 'transparent',
    cursor: 'pointer', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0,
  },
  recordingLabel: { flex: 1, display: 'flex', alignItems: 'center', gap: 8, color: '#C0392B', fontWeight: 600, fontSize: 15, minHeight: 44 },
  recordDot: { width: 10, height: 10, borderRadius: '50%', background: '#E74C3C', display: 'inline-block', animation: 'pulse 1s infinite' },
  callRecord: { background: '#EEF6F6', color: '#0B5F63', borderRadius: 999, padding: '6px 14px', fontSize: 13, fontWeight: 600 },
  uploadBar: { display: 'flex', flexDirection: 'column', gap: 4, padding: '0 8px' },
  uploadTrack: { height: 4, background: '#E0E0E0', borderRadius: 4, overflow: 'hidden' },
  uploadFill: { height: '100%', background: '#17A2A9', transition: 'width 0.2s' },
  emptyChat: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyChatText: {
    fontSize: '18px',
    color: '#5B6E6E',
  },
  emptyText: {
    textAlign: 'center',
    color: '#5B6E6E',
    fontSize: '14px',
    padding: '20px',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '70vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #E0E0E0',
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#072B2C',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#5B6E6E',
    padding: '0',
  },
  modalContent: {
    padding: '20px',
  },
  searchSection: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    padding: '12px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '2px solid #17A2A9',
  },
  searchButton: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 18px',
    backgroundColor: '#17A2A9',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
  },
  usersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  userItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#F5EDE1',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  userAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  userName: {
    fontSize: '16px',
    color: '#072B2C',
    marginBottom: '2px',
  },
  userUsername: {
    fontSize: '13px',
    color: '#5B6E6E',
    margin: 0,
  },
};

export default Messaging;











